<template>
  <v-container fluid>
    <c-crud-filters v-if="withFilters" :is-mobile="$isMobile()" with-extra>
      <template v-slot:content>
        <v-row>
          <v-col cols="12" xs="12" md="3">
            <c-companies-autocomplete
              v-model="filters.company_id"
              :label="$lang('properties.filters.company')"
              clearable
            />
          </v-col>
          <v-col cols="12" xs="12" md="3">
            <v-select
              v-model="filters.year"
              label="Anno"
              :items="years"
              clearable
            />
          </v-col>
          <v-col cols="12" xs="12" md="3">
            <v-select
              v-model="filters.type"
              :label="$lang('company-invoices.fields.type')"
              :items="$in('ConstDocumentTypes')"
              clearable
            />
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" xs="12" md="3">
            <v-text-field
              v-lazy-input:debounce="700"
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              :label="$lang('main.words.search')"
              single-line
              hide-details
              clearable
            />
          </v-col>
        </v-row>
      </template>
      <template #extra>
        <v-date
          v-model="filters.from"
          :label="$lang('main.words.from')"
          clearable
        />
        <v-date
          v-model="filters.to"
          :label="$lang('main.words.to')"
          clearable
        />
        <v-select
          v-model="filters.status"
          :label="$lang('company-invoices.fields.status')"
          :items="$in('ConstCompanyInvoiceStatuses')"
          clearable
        />
      </template>
    </c-crud-filters>

    <v-skeleton-loader
      v-show="request.loading"
      :tile="false"
      :boilerplate="false"
      type="actions,table-row-divider@15"
      class="mx-auto"
    ></v-skeleton-loader>

    <v-card v-show="!request.loading" flat>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn v-if="$can('CREATE_COMPANY_INVOICES')" @click="openCreation" color="primary">
          <v-icon>mdi-plus</v-icon>
          {{ $lang("main.crud.add") }}
        </v-btn>
      </v-card-actions>
      <v-card-text>
        <v-simple-table>
          <template v-slot:default>
            <thead>
            <tr>
              <th v-if="$can('EDIT_COMPANY_INVOICES')" class="text-left sortable" @click="sort('id')">
                <v-icon v-if="filters.sortBy === 'id' && isAsc" small color="primary">mdi-sort-ascending</v-icon>
                <v-icon v-if="filters.sortBy === 'id' && isDesc" small>mdi-sort-descending</v-icon>
                <v-icon v-if="filters.sortBy !== 'id'" small>mdi-sort</v-icon>
                ID
              </th>
              <th class="text-left sortable" @click="sort('date')">
                <v-icon v-if="filters.sortBy === 'date' && isAsc" small color="primary">mdi-sort-ascending</v-icon>
                <v-icon v-if="filters.sortBy === 'date' && isDesc" small>mdi-sort-descending</v-icon>
                <v-icon v-if="filters.sortBy !== 'date'" small>mdi-sort</v-icon>
                {{ $lang("company-invoices.fields.date") }}
              </th>
              <th class="text-left sortable" @click="sort('invoice_n')">
                <v-icon v-if="filters.sortBy === 'invoice_n' && isAsc" small color="primary">mdi-sort-ascending</v-icon>
                <v-icon v-if="filters.sortBy === 'invoice_n' && isDesc" small>mdi-sort-descending</v-icon>
                <v-icon v-if="filters.sortBy !== 'invoice_n'" small>mdi-sort</v-icon>
                {{ $lang("company-invoices.fields.invoice_n") }}
              </th>
              <th class="text-left">
                {{ $lang("companies.fields.name") }}
              </th>
              <th class="text-left">
                {{ $lang('company-invoices.fields.amount') }}
              </th>
              <th v-if="$can('INTERNAL_SUPPORT')" class="text-left">
                {{ $lang('company-invoices.fields.status') }}
              </th>
              <th class="text-right">
                {{ $lang("main.crud.actions") }}
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, index) in collection" :key="index">
              <td v-if="$can('EDIT_COMPANY_INVOICES')">
                <v-chip x-small> {{ item.id }}</v-chip>
              </td>
              <td class="text-left">
                {{ $toDateFormat(item.date) }}
              </td>
              <td class="text-left">
                <v-chip
                  small
                  label
                  :color="item.type === $const('ConstDocumentTypes', 'INVOICE') ? 'teal' : 'indigo'"
                  class="white--text ma-1"
                >
                  {{$constText('ConstDocumentTypes', item.type)}}
                </v-chip>

                {{ item.invoice_n }}/{{ item.invoice_series}}
              </td>
              <td class="text-left">
                <v-icon v-if="item.company.is_validated_data" color="green" title="Dati di fatturazione validati">mdi-check-decagram</v-icon>
                <v-icon v-else color="warning" title="Dati fatturazione non validati">mdi-alert-decagram</v-icon>
                {{ item.company.name }}
              </td>
              <td class="text-left">
                {{ item.amount }}
              </td>
              <td v-if="$can('INTERNAL_SUPPORT')" class="text-left">
                <status-btn :item="item" />
              </td>
              <td class="text-right">
                <v-btn v-if="$can('INTERNAL_SUPPORT')" @click="exportXML(item.id)" small color="primary" class="mr-2">
                  <v-icon small>mdi-download</v-icon>
                  XML
                </v-btn>
                <v-btn @click="exportPDF(item.id)" small color="primary" class="mr-2">
                  <v-icon small>mdi-file-pdf-box</v-icon>
                  PDF
                </v-btn>
                <v-btn v-if="$can('INTERNAL_SUPPORT')" @click="openSend(item.id)" small color="primary" class="mr-2">
                  <v-icon small>mdi-send</v-icon>
                  SDI
                </v-btn>
                <v-tooltip bottom v-if="$can('EDIT_COMPANY_INVOICES')">
                  <template v-slot:activator="{ on }">
                    <v-icon @click="openEditing(item.id)" small class="mr-2" v-on="on">mdi-pencil</v-icon>
                  </template>
                  <span>{{ $lang("main.crud.edit") }}</span>
                </v-tooltip>
                <v-tooltip v-if="$can('DELETE_COMPANY_INVOICES')" bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      @click="openRemoving(item.id)"
                      small
                      class="mr-2"
                      v-on="on"
                    >mdi-delete</v-icon
                    >
                  </template>
                  <span>{{ $lang("main.crud.remove") }}</span>
                </v-tooltip>
              </td>
            </tr>
            <tr v-if="!collection.length">
              <td colspan="12" class="text-center">
                {{ $lang("main.crud.no_records") }}
              </td>
            </tr>
            </tbody>
          </template>
        </v-simple-table>

        <v-pagination
          v-if="pagination.total > pagination.perPage"
          v-model="pagination.page"
          :length="pagination.totalPages"
          :total-visible="10"
        />

        <v-dialog v-model="isOpenEditing" fullscreen hide-overlay transition="dialog-bottom-transition">
          <editing-form
            v-if="isOpenEditing"
            :item-id="activeId"
            @cancel="closeEditing"
            @error="failed"
            @success="updated"
          />
        </v-dialog>

        <v-dialog v-model="isOpenCreation" fullscreen hide-overlay transition="dialog-bottom-transition">
          <creation-form
            v-if="isOpenCreation"
            @cancel="closeCreation"
            @error="failed"
            @success="created"
          />
        </v-dialog>

        <v-confirmation
          v-model="isOpenRemove"
          :options="{
            loading: deletionLoading,
            yes: () => remove(activeId)
          }"
        />

        <v-confirmation
          v-model="isOpenSend"
          :options="{
            loading: sendLoading,
            yes: () => sentToSdi()
          }"
        />
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
  import {mapState} from "vuex";

  import {
    GET_COMPANY_INVOICES_PAGINATION,
    REMOVE_COMPANY_INVOICES,
    EXPORT_XML_COMPANY_INVOICES,
    EXPORT_PDF_COMPANY_INVOICES,
    COMPANY_INVOICE_SEND_TO_SDI,
  } from "@/store/company-invoices/actions";

  import crud from "@/mixins/crud";

  import EditingForm from "./EditingForm.vue";
  import CreationForm from "./CreationForm.vue";
  import StatusBtn from "./partials/StatusBtn.vue";

  export default {
    name: "CompanyInvoicesList",
    components: {
      EditingForm,
      CreationForm,
      StatusBtn,
    },
    props: {
      companyId: {
        type: Number,
        required: false,
      },
      withFilters: {
        type: Boolean,
        required: false,
        default: true
      },
    },
    mixins: [
      crud,
    ],
    data() {
      return {
        filters: {
          order: "desc",
          sortBy: "date",
          search: null,
          year: this.$moment().format('YYYY'),
        },
        years: [],
        isOpenSend: false,
        sendId: null,
        skipStoreFilters: true,
      };
    },
    beforeMount() {
      let year = this.$moment().subtract(2, 'years');

      for (let i = 0; i < 3; i++) {
        this.years.push(year.format('YYYY'));
        year.add(1, 'year');
      }
    },
    methods: {
      exportXML(id) {
        this.$store.dispatch(EXPORT_XML_COMPANY_INVOICES, id);
      },
      exportPDF(id) {
        this.$store.dispatch(EXPORT_PDF_COMPANY_INVOICES, id);
      },
      dataAction() {
        return GET_COMPANY_INVOICES_PAGINATION;
      },
      removeAction() {
        return REMOVE_COMPANY_INVOICES;
      },
      dataActionParams() {
        let params = {};

        if (this.companyId) {
          params.company_id = this.companyId;
        }

        return params;
      },
      dataActionData() {
        return {
          relations: {
            company: {
              selects: [
                '*'
              ]
            }
          }
        };
      },
      openSend(id) {
        this.isOpenSend = true;
        this.sendId = id;
      },
      sentToSdi() {
        this.$store.dispatch(COMPANY_INVOICE_SEND_TO_SDI, {
          id: this.sendId
        }).then(response => {
          this.isOpenSend = false;
          this.sendId = null;
          this.showNotification('Inviata con successo', 'success');
          this.getData();
        }).catch(response => {
          this.showNotification(response.message, 'error');
        });
      }
    },
    computed: {
      ...mapState({
        request: state => state.companyInvoices.getPaginationRequest,
        deletionLoading: state => state.companyInvoices.deletionRequest.loading,
        sendLoading: state => state.companyInvoices.sendInvoiceToSdi.loading,
      })
    },
  };
</script>

<style scoped>
  .sortable{
    cursor: pointer;
  }
</style>
